import { supabase } from '@/integrations/supabase/client';

export interface SubscriptionAgreement {
  id: string;
  company_id: string;
  shareholder_id: string | null;
  agreement_number: string | null;
  status: 'DRAFT' | 'OPEN' | 'SUBSCRIBED' | 'PAID' | 'ISSUED' | 'CANCELLED';
  shares_offered: number;
  price_per_share: number;
  share_class: string;
  total_amount: number;
  payment_terms: string | null;
  payment_due_date: string | null;
  payment_received_date: string | null;
  issued_date: string | null;
  ledger_event_id: string | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
}

export const subscriptionService = {
  async getSubscriptions(companyId: string) {
    const { data, error } = await supabase
      .from('subscription_agreements')
      .select('*, shareholders(name)')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async createSubscription(
    subscription: Omit<SubscriptionAgreement, 'id' | 'created_at' | 'updated_at' | 'total_amount'>
  ) {
    const { data, error } = await supabase
      .from('subscription_agreements')
      .insert(subscription)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateSubscription(id: string, updates: Partial<SubscriptionAgreement>) {
    const { data, error } = await supabase
      .from('subscription_agreements')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: SubscriptionAgreement['status']) {
    return this.updateSubscription(id, { status });
  },

  async recordPayment(id: string, paymentDate: string) {
    return this.updateSubscription(id, {
      status: 'PAID',
      payment_received_date: paymentDate,
    });
  },

  async issueShares(id: string, ledgerEventId: string) {
    return this.updateSubscription(id, {
      status: 'ISSUED',
      issued_date: new Date().toISOString().split('T')[0],
      ledger_event_id: ledgerEventId,
    });
  },

  async deleteSubscription(id: string) {
    const { error } = await supabase.from('subscription_agreements').delete().eq('id', id);

    if (error) throw error;
  },
};
